/**
 * The one "let guests know?" offer that follows a change of the trip's dates.
 *
 * Changing the dates (and moving events with them) does not message anybody:
 * that is the point of the opt-in in lib/tripDates. But guests do see the new
 * dates, so on a PUBLISHED trip the host is OFFERED a change notice once, after
 * the dates are saved and any moves are done, through ChangeNoticeDialog, whose
 * default is "Don't send". This module only drafts the words; nothing here sends.
 *
 * It names the new dates and, when events or dinners moved with them, each one
 * (old -> new, in the zone it is read in, labelled by its IANA id, e.g. "Europe/Lisbon",
 * the same way every other notice in Studio labels a zone). Only what a guest can see is named: an event on a draft or
 * hosts-only tile is left out (`itineraryReach`), and a dinner is included because
 * its time is part of what every guest sees on the trip page.
 */
import { momentParts, smsDate, smsDraft, SMS_MAX, type ChangeNoticeDraft } from './changeNotice';
import { itineraryAudience, itineraryReach, itineraryStatus } from './itineraryVisibility';
import { stamp } from './rezoneItinerary';
import { plural } from './plural';
import { tripDateLabel } from './reminderPlan';
import { toSmsSafe } from './smsSafeText';
import { shiftPhrase, type DinnerShift, type EventShift } from './tripDates';

const visible = (move: EventShift) => itineraryReach(itineraryStatus(move.item), itineraryAudience(move.item)) !== 'nobody';

/**
 * How many of `count` parts fit in the SMS once folded. Judged by LENGTH, never by how the text ends: a text that ends in "..."
 * is not necessarily a cut one (an event named "Sunset drinks..." is just a name), and shrinking the list for it hides events.
 */
export function partsThatFit(count: number, compose: (shown: number) => string): number {
  let shown = count;
  while (shown > 1 && toSmsSafe(compose(shown)).text.length > SMS_MAX) shown -= 1;
  return shown;
}

/** Would a guest see any of these moves? A hosts-only or draft event does not; a dinner always does (its time is on the trip page). */
export function movesGuestsWouldSee(events: readonly EventShift[], dinners: readonly DinnerShift[]): boolean {
  return events.some(visible) || dinners.length > 0;
}

export interface TripDatesRange {
  start: string;
  end: string;
}

/** "now Sat Oct 17 to Mon Oct 19 (was Fri Oct 16 to Sun Oct 18)", or just the half that changed. */
export function datesPhrase(before: Partial<TripDatesRange>, after: TripDatesRange): string {
  const startMoved = Boolean(before.start) && before.start !== after.start;
  const endMoved = Boolean(before.end) && before.end !== after.end;
  const now = `${tripDateLabel(after.start)} to ${tripDateLabel(after.end)}`;
  if (startMoved && endMoved) return `now ${now} (was ${tripDateLabel(before.start as string)} to ${tripDateLabel(before.end as string)})`;
  if (startMoved) return `now starts ${tripDateLabel(after.start)} (was ${tripDateLabel(before.start as string)})`;
  if (endMoved) return `now ends ${tripDateLabel(after.end)} (was ${tripDateLabel(before.end as string)})`;
  return `now ${now}`;
}

/** "2 events and 1 dinner booking" */
function movedPhrase(events: number, dinners: number): string {
  return [
    events > 0 ? plural(events, 'event') : null,
    dinners > 0 ? plural(dinners, 'dinner booking') : null,
  ].filter(Boolean).join(' and ');
}

export function buildTripDatesNotice(input: {
  before: Partial<TripDatesRange>;
  after: TripDatesRange;
  /** Whole days the start moved; null when it did not (only the end changed). */
  days: number | null;
  /** False when the dates themselves did not change (events were brought in line with dates changed earlier). */
  datesChanged?: boolean;
  events: readonly EventShift[];
  dinners: readonly DinnerShift[];
}): ChangeNoticeDraft {
  const events = input.events.filter(visible);
  const dinners = input.dinners;
  const phrase = datesPhrase(input.before, input.after);

  const lines: string[] = [];
  const parts: string[] = [];
  for (const move of [...events, ...dinners]) {
    const after = move.kind === 'event' ? move.startsAt || move.endsAt : move.reservedAt;
    if (!after) continue;
    const at = momentParts(after, move.zone);
    lines.push(`- ${move.label}: ${stamp(move.fromWall, true)} → ${stamp(move.toWall, true)} (${move.zone})`);
    parts.push(`${move.label}, ${smsDate(at)}, ${at.time}`);
  }

  // As many as the SMS cap allows, then "and N more" rather than a cut mid-word.
  const head = input.datesChanged === false
    ? `The schedule was updated to match the trip dates, ${tripDateLabel(input.after.start)} to ${tripDateLabel(input.after.end)}.`
    : `The trip dates changed: ${phrase}.`;
  const composeSms = (n: number) => (parts.length === 0
    ? head
    : `${head} Moved with it: ${parts.slice(0, n).join('; ')}${n < parts.length ? `; and ${parts.length - n} more` : ''}.`);
  const shown = partsThatFit(parts.length, composeSms);

  const moved = movedPhrase(events.filter((move) => move.startsAt || move.endsAt).length, dinners.filter((move) => move.reservedAt).length);
  const shift = input.days ? `, ${shiftPhrase(input.days)}` : '';
  const changed = input.datesChanged !== false;
  return {
    summary: `${head}${moved ? ` ${moved} moved with it${shift}.` : ''}`,
    subject: changed
      ? `The trip dates have changed: ${tripDateLabel(input.after.start)} to ${tripDateLabel(input.after.end)}`
      : `The schedule has been updated: ${tripDateLabel(input.after.start)} to ${tripDateLabel(input.after.end)}`,
    emailBody:
      `${changed ? `The trip dates have changed: ${phrase}.` : head}`
      + (lines.length > 0 ? ` ${moved} moved with it${shift}:\n\n${lines.join('\n')}\n` : '\n')
      + '\nSee the full, up-to-date schedule here: {url}',
    ...smsDraft(composeSms(shown)),
  };
}
